import React, { useEffect, useMemo, useRef, useState } from "react";
import { employeeService, letterTemplateService, issuedLetterService } from "../../../../services/api";
import {
  blobToBase64,
  collectEmployeeLetterEmails,
  convertDocxArrayBufferToPdfBlob,
} from "../../../../utils/letterPdfExport";
import { downloadBlobFile } from "../../../../utils/letterSampleDocx";
import { DsButton } from "../../../../design-system";

function formatPersonName(employee = {}) {
  if (!employee) return "Employee";
  if (typeof employee.name === "string" && employee.name.trim()) return employee.name.trim();
  const user = employee.user || {};
  const parts = [user.name, user.lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return employee.empCode || employee.employeeId || "Employee";
}

/**
 * After letter generate: PDF preview + Download PDF + Send mail.
 */
export default function LetterPreviewSendModal({
  open,
  onClose,
  darkMode = false,
  employee,
  letterName = "Letter",
  letterType = "appointment",
  templateKey = null,
  fileBaseName = "Letter",
  docxBlob = null,
  docxArrayBuffer = null,
}) {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendMessage, setSendMessage] = useState("");
  const [sendError, setSendError] = useState("");
  const [persistNote, setPersistNote] = useState("");
  const [emailInfo, setEmailInfo] = useState(() => collectEmployeeLetterEmails(employee));
  const urlRef = useRef("");
  const persistedRef = useRef(false);

  const displayName = useMemo(() => formatPersonName(employee), [employee]);
  const safeBase = useMemo(
    () => String(fileBaseName || letterName || "Letter").replace(/[^\w\- ]+/g, "").trim() || "Letter",
    [fileBaseName, letterName]
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const run = async () => {
      setConverting(true);
      setConvertError("");
      setSendError("");
      setSendMessage("");
      setPersistNote("");
      persistedRef.current = false;
      setPdfBlob(null);
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = "";
        setPdfUrl("");
      }

      try {
        // Refresh emails from employee profile when possible
        const empKey = employee?.rawId || employee?.id;
        if (empKey) {
          try {
            const detail = await employeeService.getEmployee(empKey);
            const empDetail = detail?.employee || detail?.data || detail;
            if (empDetail && !cancelled) {
              setEmailInfo(
                collectEmployeeLetterEmails({
                  ...employee,
                  ...empDetail,
                  personalEmail: empDetail.user?.email || empDetail.personalEmail,
                  officialEmail: empDetail.email || empDetail.officialEmail,
                  user: empDetail.user,
                })
              );
            }
          } catch {
            if (!cancelled) setEmailInfo(collectEmployeeLetterEmails(employee));
          }
        } else {
          setEmailInfo(collectEmployeeLetterEmails(employee));
        }

        let buffer = docxArrayBuffer;
        if (!buffer && docxBlob) {
          buffer = await docxBlob.arrayBuffer();
        }
        if (!buffer) throw new Error("No generated letter document available.");

        const pdf = await convertDocxArrayBufferToPdfBlob(buffer, {
          fileName: `${safeBase}.docx`,
        });
        if (cancelled) return;
        const url = URL.createObjectURL(pdf);
        urlRef.current = url;
        setPdfBlob(pdf);
        setPdfUrl(url);

        // Persist to Issued by Company as soon as preview PDF is ready
        const persistEmpId = employee?.rawId || employee?.id;
        if (persistEmpId && !persistedRef.current) {
          try {
            const pdfBase64 = await blobToBase64(pdf);
            const persistRes = await issuedLetterService.persistGenerated({
              employeeId: persistEmpId,
              letterType,
              letterName,
              templateKey: templateKey || undefined,
              fileName: `${safeBase}.pdf`,
              pdfBase64,
            });
            if (!cancelled) {
              if (persistRes?.success !== false) {
                persistedRef.current = true;
                setPersistNote("Saved to Documents → Issued by Company");
              } else {
                setPersistNote(persistRes?.message || "Could not save to Issued by Company");
              }
            }
          } catch (persistErr) {
            console.warn("Issued letter persist failed:", persistErr);
            if (!cancelled) {
              setPersistNote(
                persistErr?.response?.data?.message ||
                  persistErr?.message ||
                  "Could not save to Issued by Company"
              );
            }
          }
        }
      } catch (e) {
        console.error("Letter PDF convert failed:", e);
        if (!cancelled) {
          setConvertError(
            e?.message ||
              "Failed to convert Word→PDF. Ensure LibreOffice (or Microsoft Word on Windows) is available on the API server."
          );
        }
      } finally {
        if (!cancelled) setConverting(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = "";
      }
    };
  }, [open, docxBlob, docxArrayBuffer, employee, safeBase]);

  if (!open) return null;

  const handleDownloadPdf = () => {
    if (!pdfBlob) return;
    downloadBlobFile(pdfBlob, `${safeBase}.pdf`);
  };

  const handleDownloadDocx = () => {
    if (docxBlob) {
      downloadBlobFile(docxBlob, `${safeBase}.docx`);
      return;
    }
    if (docxArrayBuffer) {
      downloadBlobFile(
        new Blob([docxArrayBuffer], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }),
        `${safeBase}.docx`
      );
    }
  };

  const handleSendMail = async () => {
    setSendError("");
    setSendMessage("");
    if (!emailInfo.hasAny) {
      setSendError("No active email is available to send.");
      return;
    }
    if (!pdfBlob) {
      setSendError("PDF is not ready yet. Please wait for preview to finish.");
      return;
    }
    setSending(true);
    try {
      const pdfBase64 = await blobToBase64(pdfBlob);
      const res = await letterTemplateService.sendLetter({
        employeeId: employee?.rawId || employee?.id || null,
        employeeName: displayName,
        empCode: employee?.empCode || employee?.employeeId || "",
        letterName,
        letterType,
        fileName: `${safeBase}.pdf`,
        pdfBase64,
        personalEmail: emailInfo.personal || undefined,
        officialEmail: emailInfo.official || undefined,
      });
      if (res?.success === false) {
        throw new Error(res?.message || "Failed to send email");
      }
      const toList = (res?.recipients || emailInfo.recipients.map((r) => r.email)).join(", ");
      setSendMessage(`Letter emailed successfully to: ${toList}`);
    } catch (e) {
      console.error("Send letter email failed:", e);
      setSendError(
        e?.response?.data?.message || e?.message || "Failed to send letter email."
      );
    } finally {
      setSending(false);
    }
  };

  const panelClass = darkMode
    ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
    : "bg-white border-slate-200 text-slate-900";

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/45 p-3 sm:p-5">
      <div
        className={`flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border shadow-2xl ${panelClass}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h3 className="text-base font-semibold">Letter preview</h3>
            <p className="mt-0.5 text-xs opacity-70">
              {letterName} · {displayName}
              {employee?.empCode || employee?.employeeId
                ? ` (${employee.empCode || employee.employeeId})`
                : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm opacity-70 hover:opacity-100"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 bg-slate-100 dark:bg-black/20">
          {converting && (
            <div className="flex h-full items-center justify-center text-sm opacity-80">
              Converting Word → PDF (preserving template formatting)…
            </div>
          )}
          {!converting && convertError && (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-[hsl(var(--ds-danger))]">{convertError}</p>
              {(docxBlob || docxArrayBuffer) && (
                <DsButton type="button" variant="secondary" onClick={handleDownloadDocx}>
                  Download Word (.docx) instead
                </DsButton>
              )}
            </div>
          )}
          {!converting && !convertError && pdfUrl && (
            <iframe
              title="Letter PDF preview"
              src={pdfUrl}
              className="h-full w-full border-0"
            />
          )}
        </div>

        <div className="space-y-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <div className="text-xs opacity-75">
            {emailInfo.hasAny ? (
              <span>
                Emails:{" "}
                {emailInfo.official ? `Official ${emailInfo.official}` : ""}
                {emailInfo.official && emailInfo.personal ? " · " : ""}
                {emailInfo.personal ? `Personal ${emailInfo.personal}` : ""}
              </span>
            ) : (
              <span className="text-[hsl(var(--ds-danger))]">
                No active email is available to send.
              </span>
            )}
          </div>
          {sendError ? (
            <p className="text-xs text-[hsl(var(--ds-danger))]">{sendError}</p>
          ) : null}
          {sendMessage ? (
            <p className="text-xs text-emerald-700 dark:text-emerald-400">{sendMessage}</p>
          ) : null}
          {persistNote ? (
            <p
              className={`text-xs ${
                persistNote.startsWith("Saved")
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-amber-700 dark:text-amber-400"
              }`}
            >
              {persistNote}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {(docxBlob || docxArrayBuffer) && (
              <DsButton
                type="button"
                variant="secondary"
                onClick={handleDownloadDocx}
                disabled={converting}
              >
                Download Word
              </DsButton>
            )}
            <DsButton
              type="button"
              onClick={handleDownloadPdf}
              disabled={converting || !pdfBlob}
            >
              Download PDF
            </DsButton>
            <DsButton
              type="button"
              variant="secondary"
              onClick={handleSendMail}
              disabled={converting || sending || !pdfBlob}
              title={
                emailInfo.hasAny
                  ? "Send letter PDF by email"
                  : "No active email is available to send"
              }
            >
              {sending ? "Sending…" : "Send mail"}
            </DsButton>
          </div>
        </div>
      </div>
    </div>
  );
}
